/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ComponentProps } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { Case, Switch, When } from "react-if";
import { Link } from "react-router-dom";
import { FlexContainer, SVGIcon, Text, theme } from "@nordcloud/gnui";
import { ExternalLinkWithIndicator } from "~/components/ExternalLink";
import { SUCCESS_TEXT } from "~/constants";
import { getProviderTooltip } from "~/hooks/useConsoleUrl/utils";
import { showSimpleInfo } from "~/services/toast";
import { trimTextBeginning } from "~/tools";

type Props = {
  id: string;
  name: string;
  provider: string;
  providerId: string;
  url?: string | null;
  description?: string;
  type?: "accounts" | "cloud-accounts" | "estate";
};

export function IdCell(props: Props) {
  return (
    <>
      <Header {...props} />
      <Switch>
        <Case condition={props.type === "estate"}>
          {props.id && <SubHeader id={props.id} />}
        </Case>
        <Case condition={props.type === "accounts"}>
          {props.description && (
            <SubDescription description={props.description} />
          )}
        </Case>
      </Switch>
    </>
  );
}

function Header({
  id,
  name,
  provider,
  providerId,
  url = null,
  type = "estate",
}: ComponentProps<typeof IdCell>) {
  const caption = getProviderTooltip(provider);

  return (
    <FlexContainer>
      <Link to={`/${type}/${id}`}>
        {trimTextBeginning(name || providerId, 100)}{" "}
      </Link>
      <When condition={type === "estate"}>
        <CopyToClipboard
          text={providerId}
          onCopy={() => {
            showSimpleInfo(SUCCESS_TEXT.copied);
          }}
        >
          <SVGIcon
            name="copy"
            size="sm"
            css={{ cursor: "pointer", marginLeft: theme.spacing.spacing02 }}
          />
        </CopyToClipboard>
      </When>
      {url && (
        <ExternalLinkWithIndicator
          css={{ marginLeft: ".5rem" }}
          url={url}
          caption={caption}
        />
      )}
    </FlexContainer>
  );
}

function SubHeader({ id }: Pick<ComponentProps<typeof IdCell>, "id">) {
  return (
    <FlexContainer>
      <Text size="sm" tag="span" color={theme.color.text.text02}>
        {trimTextBeginning(id, 100)}
      </Text>
      <CopyToClipboard
        text={id}
        onCopy={() => {
          showSimpleInfo(SUCCESS_TEXT.copied);
        }}
      >
        <SVGIcon
          name="copy"
          size="sm"
          css={{ cursor: "pointer", marginLeft: theme.spacing.spacing02 }}
        />
      </CopyToClipboard>
    </FlexContainer>
  );
}

function SubDescription({
  description,
}: Pick<ComponentProps<typeof IdCell>, "description">) {
  return (
    <FlexContainer>
      <Text size="sm" tag="span" color={theme.color.text.text02}>
        {trimTextBeginning(description, 100)}
      </Text>
    </FlexContainer>
  );
}
